import { Fragment, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag, Icon, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { invalidatePublish, usePublishJobs, usePublishStats, type PublishJob } from './queries';
import { StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { Stat, StatStrip } from '../../components/ui/stat';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { RelativeTime } from '../../components/relative-time';
import { prettyJson, statsSummary } from '../../lib/format';
import { PublishJobDetail } from './publish-job-detail';

export function PublishSection({ canPublish, pdDomain }: { canPublish: boolean; pdDomain: string | null | undefined }) {
  const { t } = useTranslation(['publish', 'common']);
  const jobs = usePublishJobs(true);
  const anyActive = jobs.data?.some((j) => j.status === 'queued' || j.status === 'running') ?? false;
  const stats = usePublishStats(true, anyActive);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const create = useMutation(trpc.publish.createJob.mutationOptions({ onSuccess: invalidatePublish }));
  const jobAction = useMutation(trpc.publish.control.mutationOptions({ onSuccess: invalidatePublish }));

  function publishLive() {
    if (!window.confirm(t('confirmLive'))) return;
    create.mutate({ mode: 'live' });
  }

  const publishable = stats.data?.publishable ?? 0;
  const buttonsDisabled = anyActive || create.isPending || publishable === 0;
  const createBusy = (mode: 'dry_run' | 'live'): boolean => create.isPending && create.variables?.mode === mode;
  const jobBusy = (id: number, action: 'pause' | 'resume' | 'cancel'): boolean =>
    jobAction.isPending && jobAction.variables?.id === id && jobAction.variables.action === action;
  const colCount = canPublish ? 7 : 6;

  return (
    <section id="publish" className="hp-card">
      <HpCardHeader title={t('title')} subtitle={t('subtitle')} icon="upload">
        {canPublish && (
          <>
            <HpButton
              icon="eye"
              busy={createBusy('dry_run')}
              disabled={buttonsDisabled}
              onClick={() => create.mutate({ mode: 'dry_run' })}
            >
              {t('dryRun')}
            </HpButton>
            <HpButton
              variant="primary"
              icon="upload"
              busy={createBusy('live')}
              disabled={buttonsDisabled}
              onClick={publishLive}
            >
              {t('publish')}
            </HpButton>
          </>
        )}
      </HpCardHeader>

      <div className="hp-card__body bo-sections">
        <div className="bo-row">
          <StatStrip className="bo-grow">
            <Stat label={t('stats.publishable')} value={stats.data?.publishable} />
            <Stat label={t('stats.applied')} value={stats.data?.appliedTotal} />
          </StatStrip>
          {stats.data !== undefined && stats.data.unmapped > 0 && stats.data.publishable === 0 && (
            <HpTag tone="warning" title={t('unmappedTitle', { count: stats.data.unmapped })}>
              {t('unmappedTag')}
            </HpTag>
          )}
        </div>
        <ErrorAlert error={create.error ?? jobAction.error ?? stats.error ?? jobs.error} />
        {!jobs.isLoading && (jobs.data ?? []).length === 0 && <p className="bo-muted">{t('empty')}</p>}
      </div>

      {(jobs.data ?? []).length > 0 && (
        <TableScroll label={t('tableLabel')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('common:table.id')}</th>
              <th>{t('common:table.mode')}</th>
              <th>{t('common:table.status')}</th>
              <th>{t('common:table.created')}</th>
              <th>{t('common:table.finished')}</th>
              <th>{t('common:table.stats')}</th>
              {canPublish && <th>{t('common:table.actions')}</th>}
            </BrutTheadRow>
            <tbody>
              {(jobs.data ?? []).map((job: PublishJob) => {
                const isOpen = expandedId === job.id;
                const cancellable = job.status === 'queued' || job.status === 'running' || job.status === 'paused';
                return (
                  <Fragment key={job.id}>
                    <tr
                      className={isOpen ? 'bo-row--link bo-row--expanded' : 'bo-row--link'}
                      onClick={() => setExpandedId(isOpen ? null : job.id)}
                      title={t('rowTitle')}
                      aria-expanded={isOpen}
                    >
                      <td className="bo-cell-muted bo-nowrap">
                        <span className={isOpen ? 'bo-expand-toggle is-open' : 'bo-expand-toggle'} aria-hidden="true">
                          <Icon name="chevronDown" />
                        </span>
                        {job.id}
                      </td>
                      <td>
                        <HpTag tone={job.mode === 'live' ? 'danger' : 'neutral'}>
                          {job.mode === 'live' ? t('common:mode.live') : t('common:mode.dry_run')}
                        </HpTag>
                      </td>
                      <td>
                        <StatusBadge status={job.status} />
                      </td>
                      <td>
                        <RelativeTime value={job.createdAt} />
                      </td>
                      <td>
                        <RelativeTime value={job.finishedAt} />
                      </td>
                      <td className="bo-cell-note">
                        <span className="bo-clip" title={prettyJson(job.stats)}>
                          {statsSummary(job.stats)}
                        </span>
                      </td>
                      {canPublish && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <span className="bo-row">
                            {job.status === 'running' && (
                              <HpButton
                                className="bo-button--sm"
                                icon="pause"
                                busy={jobBusy(job.id, 'pause')}
                                disabled={jobAction.isPending}
                                onClick={() => jobAction.mutate({ id: job.id, action: 'pause' })}
                              >
                                {t('common:actions.pause')}
                              </HpButton>
                            )}
                            {job.status === 'paused' && (
                              <HpButton
                                className="bo-button--sm"
                                variant="secondary"
                                icon="play"
                                busy={jobBusy(job.id, 'resume')}
                                disabled={jobAction.isPending}
                                onClick={() => jobAction.mutate({ id: job.id, action: 'resume' })}
                              >
                                {t('common:actions.resume')}
                              </HpButton>
                            )}
                            {cancellable && (
                              <HpButton
                                className="bo-button--sm bo-danger"
                                icon="stop"
                                busy={jobBusy(job.id, 'cancel')}
                                disabled={jobAction.isPending}
                                onClick={() => jobAction.mutate({ id: job.id, action: 'cancel' })}
                              >
                                {t('common:actions.cancel')}
                              </HpButton>
                            )}
                          </span>
                        </td>
                      )}
                    </tr>
                    {isOpen && (
                      <tr className="bo-row--expanded">
                        <td colSpan={colCount} className="bo-cell-detail">
                          <PublishJobDetail jobId={job.id} pdDomain={pdDomain} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </BrutTable>
        </TableScroll>
      )}
    </section>
  );
}
